import { DomSanitizer, SafeHtml } from "../../../../node_modules/@angular/platform-browser";
import { InternationalisationService } from "../../core/forked/angular-internationalisation";
import { CoreLanguageManager } from "../../core/services/ILanguageManager";
import { ProductDetailsHelper } from "../../core/services/product_details_helper.service";
import { ContentType, ImageOrientation, orderTypes, TranslationContentType } from "../../core/shared/contentTypes";
import { ArticleTagDataModel, BaseUnit, NutritionViewModel, ProductDataModel4, ProductDetailsDataModel3, ProductExtraItemViewModel, ProductExtraPricesModel, ProductExtraViewModel, ProductPricesModel, ScheduledForRequest, ScheduleView } from "../../core/shared/models/product.model";
import Utils from "../../core/shared/utils";
import { ISOTService } from "../services";
import { ISOTProductDetailViewModel, ISOTProductTagViewModel, ISOTProductViewModel } from "./product.facade";

function selectValueByLocale(germanValue: string, englishValue: string): string {
  return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(englishValue) ? germanValue : englishValue;
}

/** Artikel ViewModel */
export class SOTProductViewModel implements ISOTProductViewModel {

  public get is_recipe_linking(): boolean {
    return false; // Ist keine Rezepturverkettung
  }

  /** Artikel Produktidentifkationsnummer */
  public get product_id(): number {
    return this._model.product_id;
  }
  /** Artikelnummer */
  public get artikel_nr(): number {
    return this._model.artikel_nr;
  }
  /** Freie Item Nummer */
  public get free_item_nr(): number {
    return this._model.free_item_nr;
  }
  /** Artikelbezeichnung */
  public get label(): string {
    const _label = selectValueByLocale(this._model.label_de, this._model.label_en);
    return CoreLanguageManager.translate(TranslationContentType.Article_Bontext, this._model.id, _label);
  }
  /** Hat Extras als Subabfragen */
  public get has_extras(): boolean {
    return this._model.hasExtras;
  }
  /** Prüfung ob der Artikel verfügbar ist anhand des Verfügbarkeitsmanagments */
  public get is_available(): boolean {
    const me = this;
    let available = me._sotService.AvailabilityIsProductAvailable(me.product_id);
    // prüfung ob alle zwangsextras verfügbar sind (auch einzel extras/subextras)
    if (available && me.extraGroups.length > 0) {
      for (let index = 0; index < me.extraGroups.length; index++) {
        const extragroup = me.extraGroups[index];
        // einzel extra/subextra muss geprüft werden
        if (extragroup.extra_items.length == 1 && (extragroup.extra_items[0].sub_items.length <= 1)) {
          available = me._sotService.AvailabilityIsProductAvailable(extragroup.extra_items[0].product_id);
          if (available && extragroup.extra_items[0].sub_items.length == 1) {
            available = me._sotService.AvailabilityIsProductAvailable(extragroup.extra_items[0].sub_items[0].product_id);
          }
        }
        // im zwangsabfragen muss mindestens ein extra buchbar sein
        else if (extragroup.force_mode) {
          let has_available_item = false;
          for (let index = 0; index < extragroup.extra_items.length; index++) {
            const extra_item = extragroup.extra_items[index];
            if (extra_item.without_subitem) {
              has_available_item = me._sotService.AvailabilityIsProductAvailable(extra_item.product_id);
            } else {
              has_available_item = extra_item.sub_items.some(sub_item => me._sotService.AvailabilityIsProductAvailable(sub_item.product_id));
            }
            if (has_available_item) {
              break;
            }
          }
          available = has_available_item;
        }

        if (!available) {
          break;
        }
      }
    }
    return available;
  }
  /** Hat der Artikel ein Bild */
  public get has_image(): boolean {
    return this._model.last_image_update_at_ymd > 0;
  }
  /** ya_image Pfad zum Bild */
  public get image_path(): string {
    return !this.has_image ? "" : ContentType.sub_Product + "/" + this.product_id + "_photo_" + ImageOrientation.Landscape + "/" + this._model.last_image_update_at_ymd + "/" + this._model.last_image_update_at_hms;
  }
  /** Liste der zugeordneten Artikel TAG IDs */
  public get product_tag_ids(): number[] {
    return this._model.tag_ids ? this._model.tag_ids : [];
  }
  /** Cache Daten um nicht immer die Preise und Extras zu berechnen */
  private _cachedData: {
    useAH: boolean,
    servicePe: number,
    userPe: number,
    discountPercent: number,
    prices: ProductPricesModel,
    product_extras: SOTProductExtraGroupViewModel[]
  };

  /** 
   * Ermittelt bei Bedarf die Preise
   * ! Achtung: beinflust getProductExtras (es resettet den Cache)
   */
  public getPrices(): ProductPricesModel {
    const me = this;
    const useAH = me._sotService.GetOrderType() == orderTypes.pickup;
    const servicePe = me._sotService.GetServicePE();
    const userPe = me._sotService.GetUserPE();
    const discountPercent: number = me._sotService.GetBestDiscountPercent();

    if (!me._cachedData ||
      (me._cachedData.useAH != useAH) ||
      ((me._cachedData.servicePe != null ? me._cachedData.servicePe : -1) != (servicePe != null ? servicePe : -1)) ||
      ((me._cachedData.userPe != null ? me._cachedData.userPe : -1) != (userPe != null ? userPe : -1)) ||
      ((me._cachedData.discountPercent != null ? me._cachedData.discountPercent : 0) != (discountPercent != null ? discountPercent : 0))) {
      me._cachedData = {
        useAH: useAH,
        servicePe: servicePe,
        userPe: userPe,
        discountPercent: discountPercent,
        product_extras: null, // Muss neu ermittelt werden, weil der Preis geändert wurde
        prices: ProductDetailsHelper.calculateProductPrices(me._model,
          useAH,
          ProductDetailsHelper.calculateBestPricelevel(me._model, servicePe, userPe),
          discountPercent,
          me._model.saleable, // Extras berechnen, wenn auch verkaufsfähig
          me._details.model,
          me._sotService.coreLogService)
      };
    }
    return me._cachedData.prices;
  }

  /** 
   * Ermittelt die Extras und die Preise.
   * Man kann es umstellen, dass die Preise extra ermittelt werden, um die Extras nicht neu zu ermitteln.
   * ! Achtung: es ist von getPrices() abhängig - es nutzt denselben Cache
   */
  public get extraGroups(): SOTProductExtraGroupViewModel[] {
    const me = this;
    if (!me._cachedData || !me._cachedData.product_extras) {
      const prices = me.getPrices();
      me._cachedData.product_extras = ProductDetailsHelper.createProductExtras(me._sortiment, me._details.model, prices)
        .sort((l, r) => l.queryOrder - r.queryOrder)
        .map((ya_model) => new SOTProductExtraGroupViewModel(ya_model, me._sotProductMap));
    }

    return me._cachedData.product_extras;
  }

  public get linkedProducts(): ISOTProductViewModel[] {
    return []; // ist keine Rezepturtverkettung
  }

  /** Angewandte beste Preisebene */
  public get bestPE(): number {
    return this.getPrices().bestPE;
  }
  /** Artikelpreis mit Rabatt (ohne die extras) */
  public get price(): number {
    return this.getPrices().price;
  }
  /** artikel preis mit rabatt (ohne die extras) */
  public get discountPrice(): number {
    return this.getPrices().discountPrice;
  }
  /**
   *  Theoretischer Preis mit Rabatt
   *  (Mit ersten Auswahlen der Zwangsabfragen in Zwangsanzahl)
   */
  public get priceWithExtras(): number {
    return this.getPrices().discountPriceWithExtras;
  }
  public get deposit(): number {
    return this.getPrices().deposit;
  }
  public get has_deposit(): boolean {
    return this.deposit > 0;
  }

  public get depositWithExtras(): number {
    return this.getPrices().depositWithExtras;
  }
  public get has_depositWithExtras(): boolean {
    return this.depositWithExtras > 0;
  }
  /** Hilfsfunktion um Preise für Extras zu bekommen */
  public getExtraPrices(extra_id: number): ProductExtraPricesModel {
    return this.getPrices().extras[extra_id];
  }

  /**
   *  Anzeigepreis, welcher dem Anwender angezeigt wird
   *  (Base mit Extras und mit oder ohne Pfand)
   */
  public get show_price(): number {
    return this._sotService.deposit_view ? this.priceWithExtras + this.depositWithExtras : this.priceWithExtras;
  }

  private _sotService: ISOTService;
  private _sortiment: string; // wird zum Berechenen der Preise benötigt
  private _sotProductMap: { [key: number]: SOTProductViewModel }; // wird zum Befüllen der Extras benötigt
  private _model: ProductDataModel4;

  /** Basis */
  public get model(): ProductDataModel4 {
    return this._model;
  }

  private _details: SOTProductDetailViewModel;
  /** Details zum Artikel */
  public get details(): ISOTProductDetailViewModel {
    return this._details;
  }

  /** Enthält die durch refreshSchedule ermittelten Zeitsteuerungsinformationen */
  public schedule: ScheduleView = new ScheduleView();
  /** Aktualisiert schedule */
  public refreshSchedule(schedule_req: ScheduledForRequest): void {
    const me = this;
    me.schedule = me._model.isScheduledFor(schedule_req);
    // Erneuere auch die Zeitsteuerung der Extras
    me.extraGroups.forEach(extragroup => {
      extragroup.extra_items.forEach(extra => {
        if (extra.product) {
          extra.product.refreshSchedule(schedule_req);
        }
      })
    })
  }

  /** min force deposit for extras in group */
  public calcMenuMinForceDeposit(group: SOTProductExtraGroupViewModel): number {
    const me = this;
    let minForceDeposit = 0;
    // deposit calc ignores "no_choice" this, is already in the menuPrice of the Product 
    if (group.force_mode) {
      //minForceDeposit = Math.min(...group.extra_items.map(extra => me.calcExtraItemMenuMinForceDeposit(extra)));
      // Den niedrigsten Preis der auswählbaren Extras rausfinden.
      let minDiscountPrice = Math.min(...group.extra_items.map(extra => me.getExtraPrices(extra.id).discountPrice));
      // Den niedrigsten Pfand nur für die auswählbaren Extras mit dem niedrigsten Preis rausfinden.
      minForceDeposit = Math.min(...group.extra_items.map(extra => (minDiscountPrice == me.getExtraPrices(extra.id).discountPrice ? me.calcExtraItemMenuMinForceDeposit(extra) : Number.MAX_VALUE)));
      minForceDeposit = (minForceDeposit == Number.MAX_VALUE ? 0 : minForceDeposit);
    }
    return minForceDeposit;
  }

  /** preis des extras (mit rabatt), bzw. die der subs */
  public calcExtraItemMenuMinForceDeposit(extra: SOTProductExtraItemViewModel): number {
    const me = this;
    let minForceDeposit = 0;
    if (extra.sub_items && extra.sub_items.length > 0) {
      //minForceDeposit = extra.deposit + Math.min(...extra.sub_items.map(sub_extra => me.calcExtraItemMenuMinForceDeposit(sub_extra)));
      // Den niedrigsten Preis der auswählbaren SubExtras rausfinden.
      let minDiscountPrice = Math.min(...extra.sub_items.map(sub_extra => me.getExtraPrices(sub_extra.id).discountPrice));
      // Den niedrigsten Pfand nur für die auswählbaren SubExtras mit dem niedrigsten Preis rausfinden.
      minForceDeposit = Math.min(...extra.sub_items.map(sub_extra => (minDiscountPrice == me.getExtraPrices(sub_extra.id).discountPrice ? me.calcExtraItemMenuMinForceDeposit(extra) : Number.MAX_VALUE)));
      minForceDeposit = extra.deposit + (minForceDeposit == Number.MAX_VALUE ? 0 : minForceDeposit);
    } else {
      minForceDeposit = extra.deposit;
    }
    return minForceDeposit;
  }

  /** min force price for extras in group (if with choice) */
  public calcMenuMinForcePrice(group: SOTProductExtraGroupViewModel): number {
    const me = this;
    let minForcePrice = 0;
    if (group.force_mode && !group.hasNoChoice) {
      minForcePrice = Math.min(...group.extra_items.map(extra => me.calcExtraItemMenuMinForcePrice(extra)));
    }
    return minForcePrice;
  }

  /** preis des extras (mit rabatt), bzw. die der subs */
  public calcExtraItemMenuMinForcePrice(extra: SOTProductExtraItemViewModel): number {
    const me = this;
    const discountPrice = me.getExtraPrices(extra.id).discountPrice;
    if (extra.sub_items && extra.sub_items.length > 0) {
      return discountPrice + Math.min(...extra.sub_items.map(sub_extra => me.calcExtraItemMenuMinForcePrice(sub_extra)));
    } else {
      return discountPrice;
    }
  }

  constructor(model: ProductDataModel4, details_model: ProductDetailsDataModel3, sortiment: string, sotProductMap: { [key: number]: SOTProductViewModel }, sotService: ISOTService) {
    const me = this;
    me._sotService = sotService;
    me._sortiment = sortiment;
    me._sotProductMap = sotProductMap;
    me._model = model;
    me._details = new SOTProductDetailViewModel(details_model);
  }
  extra: string;
  has_subextras: boolean;

}

/** Artikeldetails */
export class SOTProductDetailViewModel implements ISOTProductDetailViewModel {

  public get is_recipe_linking(): boolean {
    return false;
  }

  /** CO2 */
  public get co2(): number {
    return this._model.co2;
  }

  /** Nährwerte */
  public get nutritions(): NutritionViewModel {
    const useGerman = InternationalisationService.locale === "de";
    return ProductDetailsHelper.getNutritionsViewModel(this._model, useGerman, !useGerman)
  }

  /** Beschreibung */
  public get description(): string {
    const me = this;
    const _details = selectValueByLocale(me._model.details_de, me._model.details_en);
    const details = CoreLanguageManager.translate(TranslationContentType.Article_Details, me._model.id, _details);
    return Utils.decodeHTMLEntities(details);
  }

  /** Zusatzstoffe */
  public get additives(): string[] {
    const locale = InternationalisationService.locale === "de" ? "de" : "en";
    const additives = selectValueByLocale(this._model.additives_de, this._model.additives_en);
    const additive_keys = ProductDetailsHelper.extractAdditivesAndAllergiesKeysFromText(additives);
    return ProductDetailsHelper.getTranslationsFromAdditivesAndAllergiesKeys(additive_keys, locale);
  }
  /** Allergene */
  public get allergens(): string[] {
    const locale = InternationalisationService.locale === "de" ? "de" : "en";
    const allergens = selectValueByLocale(this._model.allergens_de, this._model.allergens_en);
    const allergen_keys = ProductDetailsHelper.extractAdditivesAndAllergiesKeysFromText(allergens);
    return ProductDetailsHelper.getTranslationsFromAdditivesAndAllergiesKeys(allergen_keys, locale);
  }

  /** Inhaltsbeschreibung */
  public get item_Content(): string {
    const _item_content = selectValueByLocale(this._model.item_content, this._model.item_content_en);
    return CoreLanguageManager.translate(TranslationContentType.Article_ItemContent, this._model.id, _item_content);
  }

  /** Name des Inverkehrsbringer */
  public get item_Provider_Name(): string {
    const _item_provider_name = selectValueByLocale(this._model.item_provider_name, this._model.item_provider_name_en);
    return CoreLanguageManager.translate(TranslationContentType.Article_ItemProviderName, this._model.id, _item_provider_name);
  }

  /** Adresse vom Inverkehrsbringer */
  public get item_Provider_Addr(): string {
    const _item_provider_addr = selectValueByLocale(this._model.item_provider_addr, this._model.item_provider_addr_en);
    return CoreLanguageManager.translate(TranslationContentType.Article_ItemProviderAddr, this._model.id, _item_provider_addr);
  }

  /** Alkoholgehalt */
  public get alcohol_Content(): number {
    return this._model.item_alcohol;
  }

  /** Koffeingehalt */
  public get caffeine_Content(): number {
    return this._model.item_caffeine;
  }

  /** Alkohol/Koffeingehalt */
  public get hasContentLevel(): boolean {
    return this.alcohol_Content > 0 || this.caffeine_Content > 0;
  }
  /** Basiseinheit */
  public get item_Base_Unit(): BaseUnit {
    return this._model.item_base_unit_id;
  }

  /** Basiseinheitsfaktor */
  public get item_Base_Factor(): number {
    return this._model.item_base_factor;
  }
  /** Basispreis */
  public get item_Base_Price(): number {
    return this._model.baseprice;
  }

  /** Info des Inverkehrsbringer */
  public get hasProvider_Info(): boolean {
    return this.item_Provider_Name.length > 0 || this.item_Provider_Addr.length > 0;
  }

  private _model: ProductDetailsDataModel3;
  /** Basis */
  public get model(): ProductDetailsDataModel3 {
    return this._model;
  }

  constructor(model: ProductDetailsDataModel3) {
    const me = this;
    me._model = model;
  }

  public get prepackaged(): boolean {
    return this._model.prepackaged;
  }
}

/** Abfragengruppe */
export class SOTProductExtraGroupViewModel {
  /** Überschrift der Abfrage */
  public get title(): string {
    const me = this;
    return CoreLanguageManager.translate((TranslationContentType.ArticleExtratext_Start + me.query), me._model.extratextID, me._model.title);
  }

  /** Abfrage (gruppen)nummer */
  public query: number;
  /** Pflichtfeld */
  public force_mode: boolean;
  /** Anzahl an selektierbaren Extras */
  public countOfExtraOptions: number;
  /** Ob der Nutzer eine tatsächliche Wahl hat*/
  public hasNoChoice: boolean;
  /** Maximale Anzahl an Auswahlen. Wenn force_mode genutzt wird, ist es auch das Minimum) */
  public max_choices: number;
  /** Extras aus denen gewählt werden kann */
  public extra_items: SOTProductExtraItemViewModel[];

  private _model: ProductExtraViewModel;

  constructor(ya_model: ProductExtraViewModel, sotProductMap: { [key: number]: SOTProductViewModel }) {
    const me = this;
    me._model = ya_model;
    // todo, es müsste zu properties werden, statt
    me.query = ya_model.queryOrder;
    me.force_mode = ya_model.forceMode;
    me.max_choices = ya_model.maxChoices;
    me.extra_items = ya_model.productExtraItems.sort((l, r) => l.order - r.order)
      .filter((ya_item) => ya_item.subIndex >= 0)
      .map((ya_item) => new SOTProductExtraItemViewModel(ya_item, (ya_item.subIndex > 0 ? ya_model.productExtraItems.filter((sub_ya_item) => sub_ya_item.subIndex === -ya_item.subIndex) : []), sotProductMap));
    me.countOfExtraOptions = me.extra_items.reduce((a, b) => a + (b.without_subitem ? 1 : b.sub_items.length), 0);
    const zeroChoices = (this.countOfExtraOptions === 0);
    const oneChoice = (this.countOfExtraOptions === 1);
    me.hasNoChoice = zeroChoices || oneChoice;
  }
}

/** Abfrageartikel */
export class SOTProductExtraItemViewModel {
  /** Extra ID */
  public id: number;
  /** rez_id */
  public rez_id: number;
  /** Order */
  public order: number;
  /** Extra product_id */
  public product_id: number;
  /** Extra product */
  public product: SOTProductViewModel;

  /** Abfrageartikelbezeichnung */
  public get title(): string {
    return this._model.title;
  }
  /** Preis */
  public price: number;
  /** Pfand */
  public deposit: number;
  public get has_deposit(): boolean {
    return this.deposit > 0;
  }
  /** Falls true, dann existieren keine Subabfrageartikel oder nur ein Subabfrageartikel */
  public get without_subitem(): boolean {
    return this.sub_items.length === 0;
  }
  public get with_one_subitem(): boolean {
    return this.sub_items.length === 1;
  }

  /** Subabfrageartikel (wenn vorhanden '!without_subitem' dann sind sie zwingender Bestandteil als 1 zu 1 Beziehung) */
  public sub_items: SOTProductExtraItemViewModel[];

  private _model: ProductExtraItemViewModel;

  constructor(ya_model: ProductExtraItemViewModel, sub_ya_models: ProductExtraItemViewModel[], sotProductMap: { [key: number]: SOTProductViewModel }) {
    const me = this;
    me._model = ya_model;
    //TODO alles in Properties umwandeln. Wir speichern bereits das model
    me.id = ya_model.id;
    me.rez_id = ya_model.rez_id;
    me.order = ya_model.order;
    me.product_id = ya_model.product_id;
    me.product = sotProductMap[me.product_id];
    me.price = ya_model.price;
    me.deposit = ya_model.deposit;
    me.sub_items = sub_ya_models.sort((l, r) => l.order - r.order)
      .map((sub_ya_item) => new SOTProductExtraItemViewModel(sub_ya_item, [], sotProductMap));
  }
}

/** Artikel TAG */
export class SOTProductTagViewModel implements ISOTProductTagViewModel {

  /** Artikel TAG ID */
  public get id(): number {
    return this._model.id;
  }
  /** Sortiernummer */
  public get sort_no(): number {
    return this._model.sort_no;
  }
  /** Bezeichnung */
  public get name(): string {
    const _name = selectValueByLocale(this._model.name_de, this._model.name_en);
    return CoreLanguageManager.translate(TranslationContentType.ArticleTag_Name, this._model.id, _name);
  }
  /** Berechnete SVG HTML */
  private _svg_html: SafeHtml;
  /** SVG als HTML */
  public get svg_html(): SafeHtml {
    return this._svg_html;
  }

  private _model: ArticleTagDataModel;
  /** Basis */
  public get model(): ArticleTagDataModel {
    return this._model;
  }

  constructor(model: ArticleTagDataModel, domSanitizer: DomSanitizer) {
    const me = this;
    me._model = model;
    let svg = model.svg;
    if (model.color) {
      svg = svg.replace("<svg", `<svg style='fill: ${model.color};stroke: ${model.color};'`)
    }
    me._svg_html = domSanitizer.bypassSecurityTrustHtml(svg);
  }
}
